import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useApiHelper from "../hooks/useApiHelper";
import { IPartAssemblyResponse, IPartAssembly, IProductionFlowResponse, IMoveFlowItemRequest } from "../models/Part";

export default function usePartAssembly(categoryId?: string | undefined) {
  const { get, post, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const assemblies = useQuery({
    queryKey: ["assemblies"],
    queryFn: () => get<IPartAssemblyResponse>("/PartAssembly"),
    staleTime: 1000 * 60 * 10, //10 minutes
  });

  const assembliesByCategory = useQuery({
    queryKey: ["assemblies", categoryId],
    queryFn: () => get<IPartAssemblyResponse>(`/PartAssembly/by-category/${categoryId}`),
    enabled: !!categoryId,
    staleTime: 1000 * 60 * 10, //10 minutes
  });

  const create = useMutation(
    (assembly: IPartAssembly) => post("/PartAssembly", assembly),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["assemblies"]);
      },
    }
  );

  const update = useMutation(
    (assembly: IPartAssembly) => put(`/PartAssembly/${assembly.id}`, assembly),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["assemblies"]);
      },
    }
  );

  const updateOrder = useMutation(
    ({ id, newIndex }: { id: string; newIndex: number }) => put(`/PartAssembly/${id}/order?newIndex=${newIndex}`, {}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["assemblies"]);
      },
    }
  );

  // The production flow spans two stores - assembly order lives in the database, the two
  // stage positions in customer settings - so one endpoint owns the whole move rather than
  // the page trying to work out which one to write to.
  const flow = useQuery({
    queryKey: ["assemblies", "flow"],
    queryFn: () => get<IProductionFlowResponse>("/PartAssembly/flow"),
    staleTime: 1000 * 60 * 10, //10 minutes
  });

  const moveFlowItem = useMutation(
    (req: IMoveFlowItemRequest) => put("/PartAssembly/flow/order", req),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["assemblies"]);
      },
    }
  );

  const setStagesEnabled = useMutation(
    (enabled: boolean) => put(`/PartAssembly/flow/stages?enabled=${enabled}`, {}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["assemblies"]);
      },
    }
  );

  const updateCategoryOrder = useMutation(
    ({ assemblyId, categoryId, newIndex }: { assemblyId: string; categoryId: string; newIndex: number }) =>
      put(`/PartAssembly/${assemblyId}/category/${categoryId}/order?newIndex=${newIndex}`, {}),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["assemblies"]);
      },
    }
  );

  const remove = useMutation(
    (id: string) => del(`/PartAssembly/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["assemblies"]);
      },
    }
  );

  const toggleCategory = useMutation(
    (data: { assemblyId: string; categoryId: string }) =>
      post(
        `/PartAssembly/${data.assemblyId}/togglecategory/${data.categoryId}`,
        {}
      ),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["assemblies"]);
      },
    }
  );

  return {
    assemblies,
    assembliesByCategory,
    create,
    update,
    updateOrder,
    updateCategoryOrder,
    flow,
    moveFlowItem,
    setStagesEnabled,
    remove,
    toggleCategory
  };
}
